import apiCall from './api';

/**
 * Service pour la gestion des dépenses
 * ✅ Version avec filtrage automatique par le backend
 */
const expenseService = {
  
  /**
   * ✅ Ajouter une dépense
   */
  async addExpense(expenseData) {
    try {
      
      const response = await apiCall.post('/expenses', expenseData);
      
      if (response.success) {
      }
      
      return response;
      
    } catch (error) {
      console.error('❌ Erreur ajout dépense:', error);
      return { 
        success: false, 
        message: error.message || 'Erreur lors de l\'ajout de la dépense' 
      };
    }
  },

  /**
   * ✅ MODIFIÉ : Récupérer les dépenses du jour
   * Le backend filtre automatiquement selon le rôle et productAccessType
   */
  async getExpensesByDate(date) {
    try {
      
      // ✅ Le backend filtre automatiquement les dépenses selon l'utilisateur connecté
      const response = await apiCall.get('/expenses/today');
      
      if (response.success) {
        const expenses = response.data || [];
        return expenses;
      }
      
      console.warn('⚠️ Aucune dépense trouvée');
      return [];
      
    } catch (error) {
      console.error('❌ Erreur récupération dépenses:', error);
      return [];
    }
  },

  /**
   * ✅ Récupérer toutes les dépenses avec filtres
   * Le backend filtre automatiquement selon le rôle et productAccessType
   */
  async getAllExpenses(filters = {}) {
    try {
      
      const response = await apiCall.get('/expenses', { params: filters });
      
      if (response.success) {
        const expenses = response.data || [];
        return expenses;
      }
      
      return [];
      
    } catch (error) {
      console.error('❌ Erreur récupération dépenses:', error);
      return [];
    }
  },

  /**
   * ✅ Supprimer une dépense
   */
  async deleteExpense(expenseId) {
    try {
      
      const response = await apiCall.delete(`/expenses/${expenseId}`);
      
      if (response.success) {
      }
      
      return response;
      
    } catch (error) {
      console.error('❌ Erreur suppression dépense:', error);
      return { 
        success: false, 
        message: error.message || 'Erreur lors de la suppression' 
      };
    }
  },

  /**
   * ✅ Calculer le total des dépenses
   */
  calculateTotalExpenses(expenses) {
    if (!Array.isArray(expenses)) return 0;
    
    return expenses.reduce((total, expense) => {
      return total + (parseFloat(expense.amount) || 0);
    }, 0);
  },

  /**
   * ✅ Catégories de dépenses disponibles
   */
  getExpenseCategories() {
    return [
      { value: 'GENERAL', label: 'Dépense générale', icon: '💰' },
      { value: 'ACHAT_MARCHANDISE', label: 'Achat marchandise', icon: '📦' },
      { value: 'FRAIS_BOUTIQUE', label: 'Frais boutique', icon: '🏪' },
      { value: 'TRANSPORT', label: 'Transport', icon: '🚛' },
      { value: 'SALAIRE', label: 'Salaire', icon: '👥' },
      { value: 'EQUIPEMENT', label: 'Équipement', icon: '🛠️' },
      { value: 'MAINTENANCE', label: 'Maintenance', icon: '🔧' },
      { value: 'PUBLICITE', label: 'Publicité', icon: '📢' },
      { value: 'AUTRE', label: 'Autre', icon: '📝' }
    ];
  },

  /**
   * ✅ Obtenir le label d'une catégorie
   */
  getCategoryLabel(categoryValue) {
    const category = this.getExpenseCategories().find(
      cat => cat.value === categoryValue
    );
    return category ? category.label : categoryValue;
  },

  /**
   * ✅ Obtenir l'icône d'une catégorie
   */
  getCategoryIcon(categoryValue) {
    const category = this.getExpenseCategories().find(
      cat => cat.value === categoryValue
    );
    return category ? category.icon : '📝';
  },

  /**
   * ✅ Formater une devise
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  },

  /**
   * ✅ Formater une date
   */
  formatDate(date) {
    if (!date) return '';
    
    const d = new Date(date);
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  },

  /**
   * ✅ Valider les données d'une dépense
   */
  validateExpense(expenseData) {
    const errors = [];
    
    if (!expenseData.amount || parseFloat(expenseData.amount) <= 0) {
      errors.push('Le montant doit être supérieur à 0');
    }
    
    if (!expenseData.description || expenseData.description.trim() === '') {
      errors.push('La description est obligatoire');
    }
    
    if (!expenseData.category) {
      errors.push('La catégorie est obligatoire');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * ✅ Exporter les dépenses vers Excel
   */
  async exportExpensesToExcel(expenses, startDate, endDate, boutiqueName) {
    try {
      // Importer dynamiquement XLSX
      const XLSX = await import('xlsx');

      // Préparer les données pour l'export
      const data = expenses.map(expense => {
        const category = this.getExpenseCategories().find(
          cat => cat.value === expense.category
        );

        return {
          'Date': new Date(expense.date).toLocaleDateString('fr-FR'),
          'Heure': new Date(expense.date).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          'Description': expense.description,
          'Catégorie': category ? category.label : expense.category,
          'Créé par': expense.createdByName || '',
          'Montant (FCFA)': parseFloat(expense.amount)
        };
      });

      // Calculer le total
      const total = this.calculateTotalExpenses(expenses);

      // Ajouter une ligne de total
      data.push({
        'Date': '',
        'Heure': '',
        'Description': '',
        'Catégorie': '',
        'Créé par': 'TOTAL',
        'Montant (FCFA)': total
      });

      // Créer le workbook et la feuille
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Dépenses');

      // Définir la largeur des colonnes
      ws['!cols'] = [
        { wch: 12 }, // Date
        { wch: 8 },  // Heure
        { wch: 30 }, // Description
        { wch: 20 }, // Catégorie
        { wch: 20 }, // Créé par
        { wch: 15 }  // Montant
      ];

      // Générer le nom du fichier
      const startStr = new Date(startDate).toLocaleDateString('fr-FR').replace(/\//g, '-');
      const endStr = new Date(endDate).toLocaleDateString('fr-FR').replace(/\//g, '-');
      const fileName = `Depenses_${boutiqueName || 'Boutique'}_${startStr}_${endStr}.xlsx`;

      // Télécharger le fichier
      XLSX.writeFile(wb, fileName);

      return { success: true, fileName };

    } catch (error) {
      console.error('❌ Erreur export Excel:', error);
      return { 
        success: false, 
        message: 'Erreur lors de l\'export Excel' 
      };
    }
  }
};

export default expenseService;